import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import Tools from './components/Tools';
import Techniques from './components/Techniques';
import BreathingExercise from './components/BreathingExercise';
import AngerJournal from './components/AngerJournal';
import Resources2 from './components/Resources2';
import Footer from './components/Footer';
import SEO from './components/SEO';
import { initGA, trackPageView, trackEngagement } from './utils/analytics';
import googleAds from './utils/googleAds';

function App() {
  const [currentSection, setCurrentSection] = useState('home');

  // Initialize Google Analytics and Google Ads on component mount
  useEffect(() => {
    initGA();
    googleAds.initGoogleAds();
    trackPageView('Peaceful Mind - Anger Management', '/');
  }, []);

  // Track section changes
  useEffect(() => {
    if (currentSection !== 'home') {
      trackEngagement('section', currentSection);
      trackPageView(`Peaceful Mind - ${currentSection.charAt(0).toUpperCase() + currentSection.slice(1)}`, `/${currentSection}`);
      googleAds.setPageTypeForRemarketing(`/${currentSection}`);
    }
  }, [currentSection]);

  // SEO configuration for different sections
  const getSEOConfig = () => {
    const baseUrl = 'https://anger-management-bc009.web.app';
    const baseImage = `${baseUrl}/logo.svg`;
    
    switch (currentSection) {
      case 'tools':
        return {
          title: 'Anger Management Tools - Peaceful Mind',
          description: 'Access powerful anger management tools including breathing exercises, relaxation techniques, and stress relief methods. Free online tools for emotional regulation.',
          keywords: 'anger management tools, breathing exercises, relaxation techniques, stress relief, emotional regulation tools',
          url: `${baseUrl}/tools`,
          image: baseImage,
          structuredData: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Anger Management Tools",
            "description": "Access powerful anger management tools including breathing exercises, relaxation techniques, and stress relief methods.",
            "url": `${baseUrl}/tools`,
            "mainEntity": {
              "@type": "ItemList",
              "name": "Anger Management Tools",
              "description": "Collection of tools for anger management and emotional regulation",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Breathing Exercises",
                  "description": "Deep breathing techniques for stress relief"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Relaxation Techniques",
                  "description": "Methods to calm the mind and body"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Stress Relief Tools",
                  "description": "Tools for managing stress and anxiety"
                }
              ]
            }
          }
        };
      case 'techniques':
        return {
          title: 'Anger Management Techniques - Peaceful Mind',
          description: 'Learn proven anger management techniques including mindfulness, cognitive behavioral therapy methods, and emotional regulation strategies.',
          keywords: 'anger management techniques, mindfulness, CBT, cognitive behavioral therapy, emotional regulation, anger control methods',
          url: `${baseUrl}/techniques`,
          image: baseImage,
          structuredData: {
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Anger Management Techniques",
            "description": "Learn proven anger management techniques and strategies",
            "url": `${baseUrl}/techniques`,
            "step": [
              {
                "@type": "HowToStep",
                "name": "Mindfulness Practice",
                "text": "Practice mindfulness to become aware of your emotions"
              },
              {
                "@type": "HowToStep",
                "name": "Cognitive Restructuring",
                "text": "Identify and challenge negative thought patterns"
              },
              {
                "@type": "HowToStep",
                "name": "Emotional Regulation",
                "text": "Learn techniques to regulate emotional responses"
              }
            ]
          }
        };
      case 'breathing':
        return {
          title: 'Breathing Exercises for Anger Management - Peaceful Mind',
          description: 'Guided breathing exercises designed specifically for anger management. Learn deep breathing techniques to calm your mind and reduce stress.',
          keywords: 'breathing exercises, deep breathing, anger management breathing, stress relief breathing, guided breathing',
          url: `${baseUrl}/breathing`,
          image: baseImage,
          structuredData: {
            "@context": "https://schema.org",
            "@type": "ExercisePlan",
            "name": "Breathing Exercises for Anger Management",
            "description": "Guided breathing exercises for stress relief and anger management",
            "url": `${baseUrl}/breathing`,
            "exerciseType": "Breathing Exercise",
            "targetAudience": "People seeking anger management support"
          }
        };
      case 'journal':
        return {
          title: 'Anger Journal - Track Your Emotions - Peaceful Mind',
          description: 'Use our digital anger journal to track your emotions, identify triggers, and monitor your progress in anger management.',
          keywords: 'anger journal, emotion tracking, mood journal, anger triggers, emotional diary, progress tracking',
          url: `${baseUrl}/journal`,
          image: baseImage,
          structuredData: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Anger Journal",
            "description": "Digital journal for tracking anger and emotions",
            "url": `${baseUrl}/journal`,
            "applicationCategory": "HealthApplication",
            "featureList": [
              "Emotion tracking",
              "Trigger identification",
              "Progress monitoring",
              "Mood analysis"
            ]
          }
        };
      case 'resources':
        return {
          title: 'Anger Management Resources - Peaceful Mind',
          description: 'Comprehensive collection of anger management resources including articles, videos, professional help directories, and educational materials.',
          keywords: 'anger management resources, mental health resources, therapy resources, anger management articles, professional help',
          url: `${baseUrl}/resources`,
          image: baseImage,
          structuredData: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Anger Management Resources",
            "description": "Comprehensive collection of anger management resources and educational materials",
            "url": `${baseUrl}/resources`,
            "mainEntity": {
              "@type": "ItemList",
              "name": "Anger Management Resources",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Educational Articles",
                  "description": "Articles about anger management and mental health"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Professional Help Directory",
                  "description": "Directory of mental health professionals"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Educational Videos",
                  "description": "Video content about anger management"
                }
              ]
            }
          }
        };
      default:
        return {
          title: 'Peaceful Mind - Anger Management Tools & Techniques',
          description: 'Discover effective anger management tools, breathing exercises, journaling techniques, and resources to help you manage anger and find inner peace. Free online anger management support.',
          keywords: 'anger management, anger control, breathing exercises, stress relief, mental health, emotional regulation, anger journal, relaxation techniques, mindfulness, therapy tools',
          url: baseUrl,
          image: baseImage
        };
    }
  };

  const seoConfig = getSEOConfig();

  const renderSection = () => {
    switch (currentSection) {
      case 'tools':
        return <Tools />;
      case 'techniques':
        return <Techniques />;
      case 'breathing':
        return <BreathingExercise />;
      case 'journal':
        return <AngerJournal />;
      case 'resources':
        return <Resources2 />;
              default:
          return (
            <>
              <Hero setCurrentSection={setCurrentSection} />
              <Tools setCurrentSection={setCurrentSection} />
              <Techniques />
            </>
          );
    }
  };

  return (
    <div className="App">
      <SEO {...seoConfig} />
      <Header setCurrentSection={setCurrentSection} currentSection={currentSection} />
      <main>
        {renderSection()}
      </main>
      <Footer />
    </div>
  );
}

export default App;
